import { useEffect, useRef } from 'react';
import { completeChatSession } from '../utils/api';

// Marks a session 'abandoned' when the visitor leaves the page without
// explicitly ending the tour. Without this, sessions that aren't closed via an
// end-of-tour click (the common case — people just close the tab) stay 'Active'
// forever and never get an AI summary or lead score.
//
// We listen on pagehide only: it fires on real navigation away / tab close, but
// not on tab-switching or minimizing, so a session isn't ended just because the
// visitor briefly looks elsewhere. The request uses fetch keepalive (see
// completeChatSession) so it survives page teardown.
//
// Only fires once per session, and only once the welcome form is complete (a
// contact-less session isn't worth scoring) and the session hasn't already
// ended. The backend completion step is idempotent on lead score, so a later
// natural completion — or a duplicate pagehide — won't double-process.
const useAbandonBeacon = ({ state }) => {
  const { sessionId, isSessionEnded, conversationState } = state;

  const hasCompletedForm = !!(
    conversationState?.userInfo?.firstName
    && conversationState?.userInfo?.lastName
    && conversationState?.userInfo?.email
  );

  // The pagehide listener is registered once, so read live values via a ref.
  const dataRef = useRef({});
  dataRef.current = { sessionId, isSessionEnded, hasCompletedForm };
  const firedRef = useRef(false);

  // Reset the once-guard when a new session starts (e.g. after a restart).
  useEffect(() => {
    firedRef.current = false;
  }, [sessionId]);

  useEffect(() => {
    const handlePageHide = () => {
      const d = dataRef.current;
      if (firedRef.current || !d.sessionId || d.isSessionEnded || !d.hasCompletedForm) {
        return;
      }
      firedRef.current = true;
      completeChatSession(d.sessionId, 'abandoned');
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []);
};

export default useAbandonBeacon;
